/**
 * Static auth provider factory (singleton).
 *
 * Used as the **fallback** when dynamic partner authentication is disabled
 * (`VITE_USE_DYNAMIC_PARTNER_AUTH` is not "true"). Reads MSAL configuration
 * from environment variables and creates a single shared auth provider
 * instance for the lifetime of the app.
 *
 * Also consumed directly by the data provider, upload service, and download
 * hooks to obtain access tokens for API calls.
 *
 * @see dynamicAuthProviderInstance.ts — partner-specific alternative
 * @see msalAuthProvider.ts            — underlying MSAL implementation
 */
import { AuthProvider } from "react-admin";
import { createMsalAuthProvider } from "./msalAuthProvider";
import { mockAuthProvider } from "./mockAuthProvider";
import { USE_AUTH } from "@/config/auth";
import { getActiveAuthProvider } from "./activeAuthProvider";

let authProviderInstance: AuthProvider | null = null;

const getScopes = () => {
  if (import.meta.env.VITE_OIDC_OPTIONS_DEFAULT_SCOPES) {
    return import.meta.env.VITE_OIDC_OPTIONS_DEFAULT_SCOPES.split(",");
  }
  if (import.meta.env.VITE_OIDC_SUBSCRIPTION_SCOPES) {
    return import.meta.env.VITE_OIDC_SUBSCRIPTION_SCOPES.split(",");
  }
  return ["User.Read"];
};

/**
 * Returns the active auth provider. If a dynamic provider has been set
 * (dedicated tenant), it takes priority. Otherwise falls back to the
 * static singleton built from environment variables.
 */
export const getAuthProvider = (): AuthProvider => {
  // If a dynamic provider is active (dedicated tenant), use it
  const active = getActiveAuthProvider();
  if (active) return active;

  if (!USE_AUTH) {
    console.log("[AuthProviderInstance] Auth disabled, using mock provider");
    return mockAuthProvider;
  }

  if (!import.meta.env.VITE_MSAL_CLIENT_ID || !import.meta.env.VITE_MSAL_AUTHORITY) {
    throw new Error(
      "[AuthProviderInstance] VITE_MSAL_CLIENT_ID and VITE_MSAL_AUTHORITY are required when auth is enabled"
    );
  }

  if (!authProviderInstance) {
    console.log("[AuthProviderInstance] Creating auth provider singleton");
    authProviderInstance = createMsalAuthProvider({
      clientId: import.meta.env.VITE_MSAL_CLIENT_ID,
      authority: import.meta.env.VITE_MSAL_AUTHORITY,
      redirectUri: `${window.location.origin}/`,
      scopes: getScopes(),
      loginMethod: import.meta.env.VITE_MSAL_LOGIN_METHOD || "redirect",
      useOIDC: !!import.meta.env.VITE_OIDC_OPTIONS_DEFAULT_SCOPES,
    });
  }
  return authProviderInstance;
};
